import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { login, signup, notesFromServer, cacheUserDataToLocalStorage, isLoggedIn } from '../lib/authSync';

export default function Login() {
  const navigate = useNavigate();
  const [mode, setMode] = useState('login'); // 'login' | 'signup'
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  const title = useMemo(() => (mode === 'login' ? 'Log in' : 'Create account'), [mode]);

  useEffect(() => {
    if (isLoggedIn()) navigate('/app', { replace: true });
  }, [navigate]);

  async function onSubmit(e) {
    e.preventDefault();
    if (!username.trim() || !password) return;

    setBusy(true);
    try {
      if (mode === 'signup') {
        await signup(username.trim(), password);
        toast.success('Account created. You can log in now.');
        setMode('login');
        setPassword('');
        return;
      }

      const data = await login(username.trim(), password);
      localStorage.setItem('token', data.token);
      localStorage.setItem('userId', data.userId);

      // Reset any previous user's cached state to prevent cross-account mixing
      const resetKeys = [
        'userData',
        'userNotes',
        'anotequest_notes',
        'anotequest_folders',
        'anotequest_images',
        'anotequest_note_stickers',
        'anotequest_todos',
        'anotequest_characters',
        'anotequest_sources',
        'anotequest_tables',
        'anotequest_drawings',
        'anotequest_trash',
        'anotequest_stats',
        'anotequest_stickers',
        'anotequest_username',
        'theme',
      ];
      for (const k of resetKeys) localStorage.removeItem(k);

      const hydratedNotes = notesFromServer(data.notes);
      localStorage.setItem('userNotes', JSON.stringify(hydratedNotes));
      localStorage.setItem('anotequest_notes', JSON.stringify(hydratedNotes));

      if (data.userData) {
        cacheUserDataToLocalStorage(data.userData);
      }

      toast.success('Logged in');
      navigate('/app', { replace: true });
    } catch (err) {
      console.error('Login failed:', err);
      toast.error(err?.message || 'Authentication failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="min-h-screen bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-slate-50 flex items-center justify-center px-4 py-10">
      <Card className="w-full max-w-md p-6">
        <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
          {mode === 'login' ? 'Welcome back.' : 'Save your notes across devices.'}
        </p>

        <form className="mt-6 space-y-4" onSubmit={onSubmit}>
          <div className="space-y-1">
            <label className="text-sm font-medium">Username</label>
            <Input value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="username" />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium">Password</label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
            />
          </div>

          <Button className="w-full" type="submit" disabled={busy || !username.trim() || !password}>
            {busy ? 'Please wait…' : title}
          </Button>
        </form>

        <div className="mt-4 text-sm text-slate-600 dark:text-slate-300">
          {mode === 'login' ? (
            <button className="underline" onClick={() => setMode('signup')} type="button">
              Need an account? Sign up
            </button>
          ) : (
            <button className="underline" onClick={() => setMode('login')} type="button">
              Already have an account? Log in
            </button>
          )}
        </div>
      </Card>
    </main>
  );
}
